import { MapPin, UserRoundX } from "lucide-react";
import { type AppointmentWithContext } from "@taskiq/shared";
import { PersonAvatar } from "@/components/ui/Avatar";
import { formatDayIn, formatTimeRangeIn, formatTimeShortIn } from "@/lib/dates";
import { isOverdue, pointerFrom, type OpenAppointment } from "@/lib/schedule";
import { cn } from "@/lib/cn";

/**
 * One appointment, as it appears on the calendar.
 *
 * ── The title is the thing, and it is never what gets dropped ───────────────
 *
 * Every variant leads with the title. It used to lead with the time, which read
 * fine on a tall chip and failed on a short one: a thirty-minute job is clamped
 * to a few lines of box, the column clips whatever does not fit, and what did
 * not fit was the only text on the chip saying which job it is. The time was
 * never the information at risk - a chip's *position in the grid* already says
 * when it is, which is the one fact the layout encodes for free.
 *
 * So the order is title → time → everything else, and the clipping now takes
 * from the bottom of that list rather than the top.
 *
 * ── Colour says how urgent; it never says how it went ───────────────────────
 *
 * Priority is an ordered ramp keyed off the priority value itself - quiet, plain,
 * warm, loud - and why it is no longer `cat-1` .. `cat-6` is written out in
 * `shared/src/schemas/scheduling.ts`. The short version: those six hues are
 * picked to be maximally different from each other, which made `high` (olive)
 * read calmer than `normal` (cyan).
 *
 * **No chip writes its priority as a word.** It is one tap away in the detail
 * panel, and a badge on every high and urgent chip was the loudest element on a
 * screen whose whole job is to be skimmed. The ramp and the rail answer "is
 * anything urgent" without it.
 *
 * **Status is still never carried by hue.** Cancelled is struck through and
 * muted, completed carries a check and drops back, in-progress gets a solid left
 * bar. Overdue is the one exception: it leaves the ramp for caution amber and
 * keeps the dashed edge it always had, so the hue is reinforcement rather than
 * the message. About 8% of this workforce has a red/green deficiency, and a
 * schedule that has to be decoded by colour is a schedule some of them cannot
 * read - which is also why the accessible name below still spells the priority
 * out in full.
 *
 * ── Three variants, one per home ───────────────────────────────────────────
 *
 * `block` fills an hour cell and has the room for the assignee and the address.
 * `compact` is one line, for a month cell. `tray` is the unassigned queue, which
 * is the one place the chip is read **out of calendar context** - nothing around
 * it says which day it is - so it leads with the date instead, and gives the
 * title two lines rather than half of one.
 */
type Variant = "block" | "compact" | "tray";

export function EventChip({
  appointment,
  timezone,
  onOpen,
  variant = "block",
}: {
  appointment: AppointmentWithContext;
  timezone: string;
  onOpen: OpenAppointment;
  variant?: Variant;
}) {
  const overdue = isOverdue(appointment);
  const assignee = appointment.assignee?.full_name ?? appointment.assignee?.email ?? null;
  const range = formatTimeRangeIn(appointment.scheduled_start, appointment.scheduled_end, timezone);

  return (
    <button
      type="button"
      onClick={(event) => onOpen(appointment, event.currentTarget, pointerFrom(event))}
      // The accessible name carries what the visual chip conveys through
      // position and colour - the time, the urgency and who is on it - because
      // none of those survive being read out as a coloured rectangle.
      aria-label={[
        appointment.title,
        formatDayIn(appointment.scheduled_start, timezone),
        range,
        appointment.priority !== "normal" ? `${appointment.priority} priority` : null,
        assignee ? `assigned to ${assignee}` : "unassigned",
        appointment.status === "cancelled" ? "cancelled" : null,
        overdue ? "overdue" : null,
      ]
        .filter(Boolean)
        .join(", ")}
      className={cn(
        "event",
        `event--prio-${appointment.priority}`,
        `event--${variant}`,
        appointment.status === "cancelled" && "event--cancelled",
        appointment.status === "completed" && "event--completed",
        appointment.status === "in_progress" && "event--running",
        overdue && "event--overdue",
        !appointment.assigned_to && "event--unassigned"
      )}
    >
      {variant === "tray" ? (
        // The queue's own order: which day, how urgent, when, and then what. The
        // date leads because this is the one chip with no calendar around it to
        // say so, and a time with no date on it is unactionable.
        <>
          <span className="event__head">
            <span className="event__date">
              {formatDayIn(appointment.scheduled_start, timezone)}
            </span>
            <span className="event__time tnum">{range}</span>
          </span>
          <span className="event__title">{appointment.title}</span>
        </>
      ) : variant === "compact" ? (
        // A month cell is one line high. The title takes the room and truncates;
        // the time is a start only, right-aligned, and never squeezes it.
        <>
          <span className="event__title">{appointment.title}</span>
          <span className="event__time tnum">
            {formatTimeShortIn(appointment.scheduled_start, timezone)}
          </span>
        </>
      ) : (
        <>
          {/* The title, and whether anybody is on it. The icon rides the title's
              line because that line is the one thing never dropped - see
              `.event__assign`. */}
          <span className="event__head">
            <span className="event__title">{appointment.title}</span>
            <span className="event__assign" aria-hidden>
              {assignee ? <PersonAvatar person={appointment.assignee} size="xs" /> : <UserRoundX />}
            </span>
          </span>
          <span className="event__time tnum">{range}</span>

          <span className="event__meta">
            {assignee ? (
              <span className="event__who">{assignee}</span>
            ) : (
              // Named rather than left blank. Unassigned work is the thing a
              // dispatcher opened this screen to find, so it says so on the chip
              // instead of being the absence of a line.
              <span className="event__who event__who--none">Unassigned</span>
            )}
            {appointment.location_address && (
              <span className="event__where">
                <MapPin aria-hidden />
                {appointment.location_address}
              </span>
            )}
          </span>
        </>
      )}
    </button>
  );
}
